"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";

import { readLocaleFromCookie, writeLocaleCookie } from "@/lib/utils/locale";
import { isSupportedLocale, Locale } from "@/i18n/config";
import { useAuthStore } from "@/stores/auth-store";

/**
 * Sincroniza la cookie NEXT_LOCALE con `user.locale` cuando cambia el usuario
 * autenticado (login inicial o SSO). Estrategia:
 *
 *   - Si `user.locale` es un locale soportado y difiere de la cookie actual
 *     → escribe cookie y llama a `router.refresh()` para que RSC recarguen
 *     el catálogo.
 *   - Si `user.locale` es null o inválido → no toca la cookie (respetamos
 *     el valor manual que ya haya).
 *
 * Solo se dispara UNA vez por transición "user null → user set" para no
 * pisar cambios manuales del LocaleSwitcher durante la sesión. Se rearma
 * al hacer logout (user vuelve a null).
 */
export function useAuthLocaleSync() {
  const router = useRouter();
  const user = useAuthStore((s) => s.user);
  const lastSyncedUserRef = useRef<number | null>(null);

  useEffect(() => {
    // logout → rearmar para el próximo login.
    if (!user) {
      lastSyncedUserRef.current = null;
      return;
    }
    // Ya sincronizado para este user (no volver a pisar).
    if (lastSyncedUserRef.current === user.id) return;

    if (isSupportedLocale(user.locale)) {
      const preferred = user.locale as Locale;
      const cookieLocale = readLocaleFromCookie();
      if (cookieLocale !== preferred) {
        writeLocaleCookie(preferred);
        router.refresh();
      }
    }
    lastSyncedUserRef.current = user.id;
  }, [user, router]);
}
